import React from 'react';
import { Spinner } from 'react-bootstrap';
import { phoneVerifiedIcon } from '~/components/Profile/LoginInfo/Phone';
import { ServicesContext } from '~/services';

enum MfaOptionEnum {
    GOOGLE_AUTH = 'Google Authenticator',
    SMS = 'Text Message',
}

interface MfaInfoProps {
    user: UserType;
    retrieveUser: () => Promise<void>;
}
export function MfaInfo({ user, retrieveUser }: MfaInfoProps) {
    const services = React.useContext(ServicesContext);
    const [confirmReset, setConfirmReset] = React.useState<string[]>([]);
    const [loading, setLoading] = React.useState(false);
    const phone = user.user_phones?.[0];

    async function handleReset() {
        setLoading(true);
        if (confirmReset.includes(MfaOptionEnum.GOOGLE_AUTH)) {
            await services.api.call({
                method: 'delete',
                url: `/api/totp-authenticators`,
                data: {
                    user_id: user.id,
                    provider: 'google',
                },
            });
        }
        if (confirmReset.includes(MfaOptionEnum.SMS)) {
            await services.user.deletePhone(phone?.id);
        }
        await retrieveUser();
        setLoading(false);
    }

    function resetAll() {
        const options = [];
        if (user.two_factor_auth?.is_google_authenticator_enabled)
            options.push(MfaOptionEnum.GOOGLE_AUTH);
        if (phone) options.push(MfaOptionEnum.SMS);
        setConfirmReset(options);
    }

    return (
        <div className="card mb-2">
            <div className="card-header">
                Login Information
                <button
                    type="button"
                    className="btn btn-info btn-sm pull-right"
                    disabled={!phone && !user.two_factor_auth?.is_google_authenticator_enabled}
                    onClick={resetAll}
                >
                    Reset All
                </button>
            </div>
            <div className="card-body">
                <div className="h5">2-Step Verification</div>
                <div>
                    Google Authenticator:{' '}
                    {user.two_factor_auth?.is_google_authenticator_enabled ? (
                        <span>
                            <i
                                className="fa-solid fa-check-circle text-success me-2"
                                title="This Google Authenticator can now be used for 2-step verification when logging in"
                            />
                            (
                            <button
                                type="button"
                                className="link-button"
                                onClick={() => setConfirmReset([MfaOptionEnum.GOOGLE_AUTH])}
                            >
                                Reset
                            </button>
                            )
                        </span>
                    ) : (
                        'None'
                    )}
                </div>
                <div>
                    Text Message:{' '}
                    <span>
                        {phone?.phone.number || 'None'}
                        <span className="ms-2 me-2">{phoneVerifiedIcon(phone)}</span>
                        {phone && (
                            <>
                                (
                                <button
                                    type="button"
                                    className="link-button"
                                    onClick={() => setConfirmReset([MfaOptionEnum.SMS])}
                                >
                                    Reset
                                </button>
                                )
                            </>
                        )}
                    </span>
                </div>
                {confirmReset.length > 0 && (
                    <div className="text-center mt-3">
                        <p>{`Are you sure you want to reset this user's ${confirmReset.join(
                            ' and '
                        )} 2-step verification method${confirmReset.length > 1 ? 's' : ''}?`}</p>
                        <div>
                            <button
                                className="btn btn-secondary me-3"
                                onClick={() => setConfirmReset([])}
                                type="button"
                            >
                                Cancel
                            </button>
                            <button
                                className="btn btn-danger"
                                disabled={loading}
                                onClick={handleReset}
                                type="button"
                            >
                                {loading && (
                                    <Spinner
                                        animation="border"
                                        variant="light"
                                        size="sm"
                                        role="status"
                                        className="me-1"
                                    />
                                )}
                                Yes, Reset
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
